const API_BASE_URL = `${window.neptune.getAzureFunctionEnv()}/api/ProxyGetStoreStockNewAsync2`;
const API_TIMEOUT_MS = 15000;
const SEARCH_DEBOUNCE_MS = 700;
const MIN_POSTCODE_LENGTH = 3;
const SUBMIT_COOLDOWN_MS = 500;
const LOG_PREFIX = '[StoreAvailability]';

if (!customElements.get('store-availability-drawer')) {
  customElements.define(
    'store-availability-drawer',
    class StoreAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();
        this.mastercode = null;
        this.config = null;
        this.storeTemplates = new Map();
        this.abortController = null;
        this.searchDebounceTimer = null;
        this.isLoading = false;
        this.hasInitialized = false;
        this.lastSubmitTime = 0;
        this.lastSearchedPostcode = '';
        this.calls = 0;
        this.lastUrl = '';
        this.handleSearchInput = this.handleSearchInput.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleRetry = this.handleRetry.bind(this);
        this.close = this.close.bind(this);
      }

      connectedCallback() {
        if (this.hasInitialized) return;

        this.drawer = this.querySelector('[data-store-availability-drawer]');
        this.listContainer = this.querySelector('[data-store-list-container]');
        this.searchInput = this.querySelector('#store-availability-search-input');
        this.searchForm = this.querySelector('.store-availability-flyout__search form');
        this.closeBtn = this.querySelector('[data-store-availability-close]');
        this.loadingSpinner = this.querySelector('[data-loading-spinner]');
        this.errorMessage = this.querySelector('[data-error-message]');
        this.noResultsMessage = this.querySelector('[data-no-results]');
        this.retryBtn = this.querySelector('[data-retry]');
        this.messages = this.querySelectorAll('.js-store-messages');
        this.onDisplayMessage = this.querySelector('.js-on-display');
        this.inStockMessage = this.querySelector('.js-in-stock');
        this.flyout = this.querySelector('.store-availability-flyout');
        this.renderedHtml = '';
        this.overlayClose = this.querySelector('[data-close-overlay]');
        this.mastercode = this.drawer?.dataset?.mastercode;
        this.config = this.parseConfig();
        this.setupEventListeners();
        //this.fetchStoreData();

        this.hasInitialized = true;
      }

      disconnectedCallback() {
        clearTimeout(this.searchDebounceTimer);
        this.abortController?.abort();

        this.searchInput?.removeEventListener('input', this.handleSearchInput);
        this.searchForm?.removeEventListener('submit', this.handleFormSubmit);
        this.retryBtn?.removeEventListener('click', this.handleRetry);
        this.closeBtn?.removeEventListener('click', this.close);
      }

      parseConfig() {
        const configScript = this.querySelector('script[custom-data]');
        if (!configScript) return { classes: { stock: {} }, translations: { stock: {} } };

        try {
          return JSON.parse(configScript.textContent);
        } catch (e) {
          console.error(`${LOG_PREFIX} Failed to parse config:`, e);
          return { classes: { stock: {} }, translations: { stock: {} } };
        }
      }

      setupEventListeners() {
        this.searchInput?.addEventListener('input', this.handleSearchInput);
        this.searchForm?.addEventListener('submit', this.handleFormSubmit);
        this.retryBtn?.addEventListener('click', this.handleRetry);
        this.closeBtn?.addEventListener('click', this.close);
        this.overlayClose?.addEventListener('click', this.close);
      }

      handleFormSubmit(e) {
        e.preventDefault();
        const now = Date.now();

        if (now - this.lastSubmitTime < SUBMIT_COOLDOWN_MS) return;

        this.lastSubmitTime = now;
        clearTimeout(this.searchDebounceTimer);

        const postcode = this.searchInput.value.trim();
        if (postcode.length >= MIN_POSTCODE_LENGTH && postcode !== this.lastSearchedPostcode) {
          this.lastSearchedPostcode = postcode;
          this.fetchStoreData();
        }
      }

      handleSearchInput(e) {
        clearTimeout(this.searchDebounceTimer);

        const postcode = e.target.value.trim();

        if (postcode.length === 0) {
          if (this.lastSearchedPostcode !== '') {
            this.lastSearchedPostcode = '';
            this.fetchStoreData();
          }
          return;
        }

        if (postcode.length < MIN_POSTCODE_LENGTH) return;

        this.searchDebounceTimer = setTimeout(() => {
          if (postcode === this.lastSearchedPostcode) return;

          this.lastSearchedPostcode = postcode;
          this.fetchStoreData();
        }, SEARCH_DEBOUNCE_MS);
      }

      async fetchStoreData(disableLoading = false, userTriggered = false) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const timeoutId = setTimeout(() => this.abortController.abort(), API_TIMEOUT_MS);

        if (!disableLoading) this.showLoading();

        try {
          const url = new URL(API_BASE_URL);

          if (this.calls > 0) {
            this.mastercode = window.neptune.variant.getVariant().mastercode;
          }

          if (userTriggered) {
            url.searchParams.append('user_triggered', true);
          }

          url.searchParams.append('mastercode', this.mastercode);

          const postcode = this.searchInput?.value.trim();
          if (postcode.length >= MIN_POSTCODE_LENGTH) {
            url.searchParams.append('postcode', postcode);
          }

          console.log(`Store stock search using: ${url.toString()}`);

          if (this.url != null && this.url.toString() == url.toString()) {
            this.listContainer.innerHTML = this.renderedHtml;
            this.hideLoading();
            console.log(
              `${this.url.toString()} -> Url has not changed will display existing data, cancelling loading...`,
            );
            return false;
          }

          const response = await fetch(url.toString(), {
            signal: this.abortController.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) throw new Error(`API error: ${response.status}`);

          const data = await response.json();
          this.handleResponse(data);
          this.calls += 1;
          this.url = url.toString();
        } catch (error) {
          clearTimeout(timeoutId);

          if (error.name !== 'AbortError') {
            console.error(`${LOG_PREFIX} Fetch failed:`, error);
            this.showError();
          }
        }
      }

      generateStoreHtml(r) {
        const stockstatus = this.getStockStatus(r);
        const escapedModel = JSON.stringify(r)
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');

        let metaobjectStore = window.neptunestores.find(e => e.storeId == r.neptuneStoreID);

        // Gets around data issue between datawarehouse and neptune products data.
        if (r.storeName.toUpperCase() == 'BURYSE') {
          r.storeName = `Bury St Edmunds`;
        }

        const html = `<li class="saf-store-list__item" data-model="${escapedModel}">
            <article class="saf-store-item">
              <a href="${metaobjectStore?.storePage}" class="saf-store-item__image-wrapper">
                <img src="${metaobjectStore?.image}">
              </a>
              <div class="saf-store-item__content">
                <a href="/pages/neptune-${r.storeName}" class="saf-store-item__name type-heading-4 mt0 mb0">${r.storeName}</a>
                <div class="saf-store-item__stock-status ${stockstatus ? stockstatus.className : ''}" aria-live="polite">
                  <span class="saf-store-item__stock-indicator" aria-hidden="true"></span>
                  <span class="saf-store-item__stock-text">${stockstatus ? stockstatus.text : ''}</span>
                </div>
                <div class="saf-store-item__cta-actions">
                  <a data-store-name="${r.storeName}" onclick="window.gtm.trackStoreAvailabilityClick(this)" href="tel:${r.storePhoneNumber}">${r.storePhoneNumber}</a>
                  <a data-store-name="${r.storeName}" onclick="window.gtm.trackStoreAvailabilityClick(this)" href="https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${r.googleLink}" target="_blank" rel="noopener">Open in maps</a>
                </div>
              </div>
            </article>
          </li>`;

        this.renderedHtml += html;
      }

      handleResponse(storeData) {
        this.hideLoading();
        this.hideError();
        this.hideNoResults();

        if (!storeData || storeData.length === 0) {
          this.showNoResults();
          return;
        }

        this.renderedHtml = ``;
        const inStock = storeData.filter(r => r.inStock === true);

        storeData.forEach(r => this.generateStoreHtml(r));

        this.listContainer.innerHTML = this.renderedHtml;

        this.messages.forEach(e => e.classList.add('hide'));

        if (inStock.length > 0) {
          this.inStockMessage?.classList.remove('hide');
        } else {
          this.onDisplayMessage?.classList.remove('hide');
        }

        try {
          var data = {
            store_availability_mastercode: this.mastercode,
          };
          if (this.searchInput?.value.length > 0) {
            data.store_availability_postcode = this.searchInput?.value.trim();
          }

          if (storeData.length >= 2) {
            data.store_availability_first_store = storeData[0].storeName;
            data.store_availability_second_store = storeData[1].storeName;
          }

          window.storeData = storeData;
          window.dataLayer.push({
            event: 'store_availability',
            eventModel: data,
          });
        } catch (e) {
          console.log(`${LOG_PREFIX}: error on gtm -  ${e}`);
        }
      }

      getStockStatus(store) {
        if (store.notOnDisplay) {
          return {
            key: 'not-on-display',
            className:
              this.config?.classes?.stock?.['not-on-display'] ||
              'saf-store-item__stock-status--not-on-display',
            text: this.config?.translations?.stock?.['not-on-display'] || 'Not on display',
          };
        } else if (store.onDisplay) {
          return {
            key: 'on-display',
            className:
              this.config?.classes?.stock?.['on-display'] ||
              'saf-store-item__stock-status--on-display',
            text: this.config?.translations?.stock?.['on-display'] || 'On display',
          };
        } else if (store.inStock) {
          return {
            key: 'in-stock',
            className:
              this.config?.classes?.stock?.['in-stock'] || 'saf-store-item__stock-status--in-stock',
            text: this.config?.translations?.stock?.['in-stock'] || 'In Stock',
          };
        } else if (store.lowStock) {
          return {
            key: 'low-stock',
            className:
              this.config?.classes?.stock?.['low-stock'] ||
              'saf-store-item__stock-status--low-stock',
            text: this.config?.translations?.stock?.['low-stock'] || 'Low Stock',
          };
        } else if (store.outOfStock) {
          return {
            key: 'out-of-stock',
            className:
              this.config?.classes?.stock?.['out-of-stock'] ||
              'saf-store-item__stock-status--out-of-stock',
            text: this.config?.translations?.stock?.['out-of-stock'] || 'Out of Stock',
          };
        }
      }

      showLoading() {
        this.isLoading = true;
        this.loadingSpinner?.classList.remove('hide');
        this.loadingSpinner?.setAttribute('aria-busy', 'true');
        this.listContainer.innerHTML = '';
        this.messages.forEach(r => r.classList.add('hide'));
      }

      hideLoading() {
        this.isLoading = false;
        this.loadingSpinner?.classList.add('hide');
        this.loadingSpinner?.setAttribute('aria-busy', 'false');
      }

      close() {
        this.flyout?.classList.toggle('active');
        this.overlayClose.classList.toggle('hide');
      }

      showError() {
        this.hideLoading();
        this.errorMessage?.classList.remove('hide');
        this.retryBtn?.focus();
      }

      hideError() {
        this.errorMessage?.classList.add('hide');
      }

      handleRetry() {
        this.hideError();
        this.fetchStoreData();
      }

      showNoResults() {
        if (this.listContainer) this.listContainer.innerHTML = '';
        this.noResultsMessage?.classList.remove('hide');
      }

      hideNoResults() {
        this.noResultsMessage?.classList.add('hide');
      }
    },
  );
}

console.log(`${LOG_PREFIX} Completed`);
